import { DAY_ORDER, TIME_ZONES } from '@/data'
import { formatMilitaryTimeWithoutMinutes } from '@/utils/format/dates'

export const getIfRestaurantIsOpen = (timeZone?: string, hours?: { [key: string]: OpenCloseHours }) => {
  if (!hours || !timeZone) return
  if (typeof hours === 'string') return

  const restaurantDateString = new Date().toLocaleString('en-US', {
    timeZone: TIME_ZONES[timeZone as keyof typeof TIME_ZONES].zone,
  })

  const restaurantDate = new Date(restaurantDateString)
  const restaurantDayName = restaurantDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
  const restaurantHoursByDay = hours[restaurantDayName as keyof typeof hours]

  if (!restaurantHoursByDay?.open || !restaurantHoursByDay?.close) return

  const restaurantOpeningDate = new Date(restaurantDateString)
  const restaurantClosingDate = new Date(restaurantDateString)
  const restaurantCurrentDate = new Date(restaurantDateString)

  const openingHoursSplit = restaurantHoursByDay?.open.split(':')
  const openingHoursHour = openingHoursSplit[0]
  const openingHoursMinutes = openingHoursSplit[1]

  const closingHoursSplit = restaurantHoursByDay?.close.split(':')
  const closingHoursHour = closingHoursSplit[0]
  const closingHoursMinutes = closingHoursSplit[1]

  restaurantOpeningDate.setHours(parseInt(openingHoursHour))
  restaurantOpeningDate.setMinutes(parseInt(openingHoursMinutes))
  restaurantOpeningDate.setSeconds(0)

  restaurantClosingDate.setHours(parseInt(closingHoursHour))
  restaurantClosingDate.setMinutes(parseInt(closingHoursMinutes))
  restaurantClosingDate.setSeconds(0)

  const restaurentCurrentTimeMs = restaurantCurrentDate.getTime()
  const restaurantOpeningTimeMs = restaurantOpeningDate.getTime()
  const restaurantClosingTimeMs = restaurantClosingDate.getTime()

  if (restaurentCurrentTimeMs > restaurantOpeningTimeMs && restaurentCurrentTimeMs < restaurantClosingTimeMs) {
    return true
  } else {
    return false
  }
}

export const getRestaurantClosingHour = (timeZone?: string, hours?: { [key: string]: OpenCloseHours }) => {
  if (!hours || !timeZone) return
  if (typeof hours === 'string') return

  const restaurantDateString = new Date().toLocaleString('en-US', {
    timeZone: TIME_ZONES[timeZone as keyof typeof TIME_ZONES].zone,
  })

  const restaurantDate = new Date(restaurantDateString)
  const restaurantDayName = restaurantDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
  const restaurantHoursByDay = hours[restaurantDayName as keyof typeof hours]

  if (!restaurantHoursByDay.close) return ''

  return formatMilitaryTimeWithoutMinutes(restaurantHoursByDay?.close)
}

export const getMergedHoursWithHolidayHours = (
  hours: { [key: string]: OpenCloseHours },
  holidayHours?: SanityLocationHolidayHours[],
) => {
  if (!holidayHours) {
    return hours
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const rangeNumber = 7
  const dayRangeDates: Date[] = []
  const holidayHoursHashedByTime: Record<number, SanityLocationHolidayHours> = {}
  holidayHours.forEach(item => {
    const holidayDate = new Date(item.date)
    holidayDate.setHours(0, 0, 0, 0)
    holidayHoursHashedByTime[holidayDate.getTime()] = item
  })

  // Fixed for loop - added missing semicolons
  for (let index = rangeNumber * -1; index < rangeNumber; index++) {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    date.setDate(today.getDate() + index)
    dayRangeDates.push(date)
  }

  const daysByJsName: Record<number, string> = {
    0: 'SUNDAY',
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
  }

  const currentDayName = daysByJsName[today.getDay()]
  const currentDayIndex = DAY_ORDER.indexOf(currentDayName)

  const datesByDays: Record<string, { open: string | null; close: string | null }> = {}
  DAY_ORDER.forEach((day, i) => {
    const difference = currentDayIndex - i
    const date = dayRangeDates[rangeNumber - 1 - difference]

    let openingClosingHours = hours[day] || { open: null, close: null }
    const holidayHashEntry = holidayHoursHashedByTime[date.getTime()]
    if (holidayHashEntry) {
      openingClosingHours = {
        open: holidayHashEntry.isClosed ? null : holidayHashEntry.openingHour || null,
        close: holidayHashEntry.isClosed ? null : holidayHashEntry.closingHour || null,
      }
    }

    datesByDays[day] = openingClosingHours
  })

  return datesByDays
}

// NEW HOURS FORMAT FUNCTIONS

/**
 * Adjusts weekly opening hours based on holiday schedules
 * @param {WeeklyOpeningHours} weeklyOpeningHours - Regular weekly opening hours
 * @param {SanityLocationHolidayHours[]} holidayHours - Array of holiday hours
 * @returns {Object} - Adjusted weekly hours incorporating holiday schedules
 */
export function getMergedTimeRangeHoursWithTimeRangeHolidayHours(
  weeklyOpeningHours: WeeklyOpeningHours | undefined,
  holidayHours: SanityLocationHolidayHours[],
) {
  // Handle edge cases
  if (!weeklyOpeningHours || !holidayHours || !Array.isArray(holidayHours)) {
    return weeklyOpeningHours || {}
  }

  // Create a copy of the weekly hours to avoid modifying the original
  const adjustedHours = JSON.parse(JSON.stringify(weeklyOpeningHours))

  // Get today's date
  const today = new Date()

  // Array of day names in order from Sunday to Saturday
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

  // Calculate the dates for the current week (7 days starting from today)
  const currentWeekDates = []
  for (let i = 0; i < 7; i++) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)

    // Format date as YYYY-MM-DD to match the format in holidayHours
    const formattedDate = date.toISOString().split('T')[0]

    currentWeekDates.push({
      date: formattedDate,
      dayOfWeek: dayNames[date.getDay()],
    })
  }

  // Check for holidays in the current week
  for (const dayInfo of currentWeekDates) {
    // Find matching holiday for this date
    const matchingHoliday = holidayHours.find(holiday => holiday.date === dayInfo.date)

    if (matchingHoliday) {
      const dayOfWeek = dayInfo.dayOfWeek

      if (matchingHoliday.isClosed) {
        // If closed on the holiday, use a special indicator as requested
        adjustedHours[dayOfWeek] = [{ isClosed: true }]
      } else if (matchingHoliday.openingHours && matchingHoliday.openingHours.length > 0) {
        // Use the new openingHours array if available
        adjustedHours[dayOfWeek] = matchingHoliday.openingHours
      } else if (matchingHoliday.openingHour && matchingHoliday.closingHour) {
        // Fall back to legacy fields if needed
        adjustedHours[dayOfWeek] = [
          {
            open: matchingHoliday.openingHour,
            close: matchingHoliday.closingHour,
          },
        ]
      }
    }
  }

  return adjustedHours
}

/**
 * Determines if a restaurant is currently open based on its timezone and hours
 * @param {string} [timeZone] - The timezone of the restaurant (Atlantic, Eastern, Central, etc.)
 * @param {WeeklyOpeningHours} [weeklyOpeningHours] - Adjusted weekly opening hours
 * @returns {boolean} - True if the restaurant is currently open, false otherwise
 */

export function getIfRestaurantIsOpenNewFormat(timeZone: string | undefined, weeklyOpeningHours: WeeklyOpeningHours) {
  // Return false if no opening hours are provided
  if (!weeklyOpeningHours) {
    return false
  }

  // Get the current time in the restaurant's timezone
  const restaurantTZ = TIME_ZONES[timeZone as keyof typeof TIME_ZONES]?.zone
  if (!restaurantTZ) return false

  // Convert to restaurant's local time parts
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: restaurantTZ,
    hour12: false,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
  const parts = formatter.formatToParts(new Date())

  // Get day of week (0-6, Sunday is 0)
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00'

  const currentWeekday = get('weekday').toLowerCase()
  const currentHour = parseInt(get('hour'))
  const currentMinute = parseInt(get('minute'))
  const currentTimeInMinutes = currentHour * 60 + currentMinute

  const todayHours = weeklyOpeningHours[currentWeekday as keyof WeeklyOpeningHours]

  const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const currentDayIndex = dayIndex.indexOf(currentWeekday)
  const prevDayIndex = (currentDayIndex + 6) % 7
  const prevWeekday = dayIndex[prevDayIndex]

  const yesterdayHours = weeklyOpeningHours[prevWeekday as keyof WeeklyOpeningHours]

  const isInTodayHours = (todayHours || []).some(range => {
    if (!range.open || !range.close || range.isClosed) return false

    const [openH, openM] = range.open.split(':').map(Number)
    const [closeH, closeM] = range.close.split(':').map(Number)

    const openMinutes = openH * 60 + openM
    let closeMinutes = closeH * 60 + closeM

    // Detect overnight operation:
    // - Close time is 00:00 (midnight)
    // - Close time is earlier than open time (e.g., 01:30 < 06:00)
    const isOvernightOperation = closeMinutes === 0 || closeMinutes < openMinutes

    if (isOvernightOperation) {
      // For midnight (00:00), treat as end of day
      if (closeMinutes === 0) {
        closeMinutes = 24 * 60
      }

      // Restaurant is open if current time is after opening OR before closing (next day)
      return currentTimeInMinutes >= openMinutes || currentTimeInMinutes < closeMinutes
    } else {
      // Normal case: restaurant opens and closes on same day
      return currentTimeInMinutes >= openMinutes && currentTimeInMinutes < closeMinutes
    }
  })

  const isInYesterdaysLateHours = (yesterdayHours || []).some(range => {
    if (!range.open || !range.close || range.isClosed) return false

    const [openH, openM] = range.open.split(':').map(Number)
    const [closeH, closeM] = range.close.split(':').map(Number)

    const openMinutes = openH * 60 + openM
    let closeMinutes = closeH * 60 + closeM
    if (closeMinutes === 0) closeMinutes = 24 * 60

    // If restaurant closes after midnight
    if (closeMinutes < openMinutes) {
      // Check if current time is before closing (i.e., after midnight)
      return currentTimeInMinutes < closeMinutes
    }

    return false
  })

  return isInTodayHours || isInYesterdaysLateHours
}
